import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "@outpave/ds";
import {
  BEGIN_REWARDS_BANK_LINK,
  SAVE_REWARDS_BANK_LINK,
} from "../../../graphql/company";
import PlaidBankLink from "../../platform/rewards/PlaidBankLink";
import type { RewardsBankLinkProps } from "./RewardsBankLink";

interface SaveRewardsBankLinkResult {
  accountIds: string[];
  skipped: {
    name: string | null;
    last4: string | null;
    reason: string | null;
  }[];
}

const PlaidRewardsBankLink: React.FC<RewardsBankLinkProps> = ({
  renderTrigger,
  onBankAdded,
  refetchBankAccounts,
  onActiveChange,
}) => {
  const { t } = useTranslation();
  const [linkToken, setToken] = useState<string | null>(null);
  const setLinkToken = (token: string | null) => {
    setToken(token);
    onActiveChange?.(Boolean(token));
  };
  const [beginLink, { loading: beginning }] = useMutation(
    BEGIN_REWARDS_BANK_LINK,
  );
  const [saveLink, { loading: saving }] = useMutation(SAVE_REWARDS_BANK_LINK);

  const handleOpen = async () => {
    try {
      const { data } = await beginLink();
      setLinkToken(data?.beginRewardsBankLink?.plaidLinkToken ?? null);
    } catch (error) {
      toast.error(t("RewardsBankLinkFailed"));
    }
  };

  const handleLinked = async (
    plaidPublicToken: string,
    plaidAccountIds: string[],
  ) => {
    setLinkToken(null);
    try {
      const { data } = await saveLink({
        variables: { plaidPublicToken, plaidAccountIds },
      });
      const result: SaveRewardsBankLinkResult | undefined =
        data?.saveRewardsBankLink;
      toast.success(t("Rewards bank linked"));
      for (const account of result?.skipped ?? []) {
        toast.warning(
          t("RewardsBankNotAdded", {
            name: account.name ?? account.last4 ?? "",
            reason: account.reason ?? "",
          }),
        );
      }
      refetchBankAccounts();
      const [firstId] = result?.accountIds ?? [];
      if (onBankAdded && firstId) onBankAdded(firstId);
    } catch (error) {
      toast.error(t("RewardsBankLinkFailed"));
    }
  };

  return (
    <>
      {renderTrigger(handleOpen, beginning || saving)}
      {linkToken && (
        <PlaidBankLink
          token={linkToken}
          onLinked={handleLinked}
          onExit={() => setLinkToken(null)}
        />
      )}
    </>
  );
};

export default PlaidRewardsBankLink;
